/**
 * Brand-stage drawer · Sprint 2026-06-11 PM-9 Phase 1B.
 *
 * Mirror of `StaleRowsDrawer` but consumes
 * `/api/brand-studio/stage-rows?stage=<id>` so Brand pillars / Foundation
 * children get an "OLDEST AT THIS STAGE" popover identical to the
 * Sales-Studio / Marketing-Studio pattern paco approved.
 *
 * Auto-closes on outside click + Esc. ~120 LOC budget.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { X, Loader2, Clock, ArrowRight } from "lucide-react";
import { api } from "../../lib/api";
import { useLanguage } from "../../contexts/LanguageContext";

function relativeTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (isNaN(t)) return "—";
  const diff = Date.now() - t;
  const days = Math.floor(diff / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

export default function BrandStageDrawer({ stage, count, accent, anchorRef, onClose }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const popoverRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    const anchor = anchorRef?.current;
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    const w = 288; // matches w-72
    const left = Math.max(8, Math.min(window.innerWidth - w - 8, r.left));
    setPos({ top: r.bottom + 6, left });
  }, [anchorRef]);

  useEffect(() => {
    api.get(`/brand-studio/stage-rows?stage=${stage}&limit=3`)
      .then(r => setRows(r.data?.rows || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [stage]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (popoverRef.current?.contains(e.target)) return;
      if (anchorRef?.current?.contains(e.target)) return;
      onClose?.();
    };
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose, anchorRef]);

  const open = (r) => {
    if (r.href) {
      onClose?.();
      navigate(r.href);
    }
  };

  const safeAccent = accent || "#6366F1";

  return createPortal(
    <div
      ref={popoverRef}
      className="fixed z-[1000] w-72 rounded-xl border bg-[var(--app-surface)] shadow-2xl p-3"
      style={{ top: pos.top, left: pos.left, borderColor: `${safeAccent}40` }}
      onClick={(e) => e.stopPropagation()}
      data-testid={`brand-stage-drawer-${stage}`}
      data-stage={stage}
    >
      <div className="flex items-center justify-between mb-2">
        <p
          className="text-[10px] uppercase tracking-widest font-bold flex items-center gap-1"
          style={{ color: safeAccent }}
        >
          <Clock className="w-3 h-3" /> {t("Oldest at this stage")}
        </p>
        <button
          onClick={onClose}
          className="p-0.5 rounded text-[var(--app-text-muted)] hover:text-[var(--app-text)]"
          aria-label="Close"
          data-testid={`brand-stage-drawer-close-${stage}`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      {loading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="w-4 h-4 animate-spin" style={{ color: safeAccent }} />
        </div>
      ) : (rows || []).length === 0 ? (
        <p className="text-xs text-[var(--app-text-muted)] py-3 text-center">
          {t("No items here right now.")}
        </p>
      ) : (
        <>
          <ul className="space-y-1.5">
            {rows.map((r, i) => (
              <li key={r.id || i}>
                <button
                  onClick={() => open(r)}
                  className="w-full text-left rounded-lg border border-[var(--app-border)] bg-[var(--app-bg)] px-2.5 py-2 transition-colors flex items-center gap-2 hover:border-[var(--app-text-muted)]"
                  data-testid={`brand-stage-row-${stage}-${i}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-[var(--app-text)] truncate">{r.title}</p>
                    <p className="text-[10px] text-[var(--app-text-muted)] truncate">
                      {r.sub}
                      {r.sub && " · "}
                      {relativeTime(r.updated_at)}
                    </p>
                  </div>
                  <ArrowRight className="w-3 h-3 shrink-0" style={{ color: `${safeAccent}aa` }} />
                </button>
              </li>
            ))}
          </ul>
          {count > 3 && (
            <p className="text-[10px] text-[var(--app-text-muted)] text-center mt-2 pt-2 border-t border-[var(--app-border)]">
              {t("Showing 3 of")} <span className="font-bold" style={{ color: safeAccent }}>{count}</span> · {t("open the section to see all")}
            </p>
          )}
        </>
      )}
    </div>,
    document.body
  );
}
